import { useStaticUrls } from 'platform-components';
import { getIsTeacher } from 'platform-components/src/components/user/selectors';
import { getExactPathMatch } from 'platform-components/utils';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import ym from 'react-yandex-metrika';

import { teacherKPKModalModel } from '@edu-frontend/modules/src/features/teacherKPKList';

import { teamBuilderActions } from 'common.components/team-builder/actions';
import { getIsStarted as getIsTeamBuilderStated } from 'common.components/team-builder/selectors';
import { TEAM_BUILDER_ENTRY_STEPS } from 'common.components/team-builder/typings';

import { actions as teacherKPKActions } from '../store/actions';
import { teacherKPKsSelectors } from '../store/selectors';
import { KPK_JOIN_ERROR_IDS } from '../constants';
import { checkIsCodeValid, findCodeInCourses } from '../utils';

import { useGoToClassroom } from './useGoToClassroom';

/** Возвращает обработчик присоединения к курсу КПК по коду */
export const useJoinCourseByCode = () => {
    const { pathname } = useLocation();

    const dispatch = useDispatch();

    const isTeacher = useSelector(getIsTeacher);
    const isTeamBuilderStarted = useSelector(getIsTeamBuilderStated);

    // Здесь ищем по всем КПК, даже если не подходят по классу
    const extendedKPKs = useSelector(teacherKPKsSelectors.getTeacherKPKs);
    const kpksLoading = useSelector(teacherKPKsSelectors.getGetTeacherKPKsLoading);

    const [delayedJoinCode, setDelayedJoinCode] = useState<string | null>(null);

    const staticUrls = useStaticUrls();

    const goToClassroom = useGoToClassroom();

    const handleJoinCourse = useCallback(
        // eslint-disable-next-line complexity
        (code: string) => {
            // Если кода нет – показываем ошибку
            if (!code) {
                dispatch(teacherKPKActions.setTeacherKPKJoinByCodeErrorId(KPK_JOIN_ERROR_IDS.CODE_IS_EMPTY));
                return;
            }

            // Если нет роли учителя – открываем модалку создания класса, после которой добавляем курс
            if (!isTeacher) {
                ym('reachGoal', 'click_add_class', { from: getExactPathMatch(staticUrls, pathname)?.path || '' });

                dispatch(
                    teamBuilderActions.start({
                        entryPoint: TEAM_BUILDER_ENTRY_STEPS.TEAM,
                        onSuccess: () => dispatch(teacherKPKActions.getTeacherKPKsRequest()),
                    })
                );
                setDelayedJoinCode(code);
                return;
            }

            // Если ввели неправильный код
            if (!checkIsCodeValid(code)) {
                dispatch(teacherKPKActions.setTeacherKPKJoinByCodeErrorId(KPK_JOIN_ERROR_IDS.CODE_INVALID));
                return;
            }

            // Нашли курс, который соответствует введеному коду
            const foundCourse = findCodeInCourses(code, extendedKPKs);

            // Если курс не нашелся
            if (!foundCourse) {
                dispatch(teacherKPKActions.setTeacherKPKJoinByCodeErrorId(KPK_JOIN_ERROR_IDS.COURSE_NOT_FOUND));
                return;
            }

            // Если курс уже добавлен
            if (foundCourse.courseId) {
                dispatch(teacherKPKActions.setTeacherKPKJoinByCodeErrorId(KPK_JOIN_ERROR_IDS.CODE_ALREADY_USED));
                return;
            }

            const onJoin = () => {
                dispatch(
                    teacherKPKActions.postTeacherKPKJoinByCodeRequest({
                        code,
                        courseTemplateId: foundCourse.courseTemplateId,
                        onSuccess: (courseId: number) => {
                            goToClassroom(courseId);

                            teacherKPKModalModel.events.closeKPKModal();
                        },
                    })
                );
            };

            const openJoinModal = () => {
                teacherKPKModalModel.events.setKPKModal({
                    opened: 'join',
                    course: foundCourse,
                    onClick: onJoin,
                });
            };

            // Если есть детали – показываем модалку с ними, а затем присоединения
            if (foundCourse.details) {
                teacherKPKModalModel.events.setKPKModal({
                    opened: 'details',
                    course: foundCourse,
                    onClick: foundCourse.hasOffer ? openJoinModal : onJoin,
                });
            } else if (foundCourse.hasOffer) {
                // Если нет – сразу показываем модалку присоединения
                openJoinModal();
            } else {
                // Если нет никаких модалок, то сразу присоединяем
                onJoin();
            }
        },
        [extendedKPKs, dispatch, goToClassroom, isTeacher, pathname, staticUrls]
    );

    // при закрытии модалки с логинами учеников team builder останавливается
    // в этот момент инициируем отправку промокода курса
    useEffect(() => {
        if (isTeacher && delayedJoinCode && !isTeamBuilderStarted && !kpksLoading) {
            handleJoinCourse(delayedJoinCode);

            setDelayedJoinCode(null);
        }
    }, [delayedJoinCode, isTeamBuilderStarted, handleJoinCourse, isTeacher, kpksLoading]);

    return handleJoinCourse;
};
